import React, { useCallback, useMemo } from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import fr from 'facet-rest';
import { FDStack } from '@facet-wealth/design';
import SearchableFinancialInstitutionList
  from 'Components/ComponentLibrary/Organisms/LinkOrAddAccounts/InstitutionIcons/SearchableFinancialInstitutionList';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { DialogContent } from 'Views/Page/Content/Modal/Components';
import { useSnackbarContext } from 'Context/SnackbarContext';

export const FacetPlanVirtualAccountSelectInstitutionStepTemplate = () => {
  const { resource } = useMetaLayoutContext();
  const { loadModalCollectionByLink, setModalCollection } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();

  const { onClickManualInstitutionAdd, manualInstitutionAddDescription, manualInstitutionAddPrompt } = resource.withRelationship(
    fr.relationships.manuallyAddInstitution.name,
    (rel) => ({
      manualInstitutionAddDescription: rel.meta('description'),
      manualInstitutionAddPrompt: rel.meta('prompt'),
      async onClickManualInstitutionAdd() {
        loadModalCollectionByLink(rel.link('related').url());
      },
    }),
    () => ({}),
  );

  const startingInstitutions = useMemo(() => resource.attr('financialInstitutionList')?.resources()?.list(), [resource]);

  const onClickInstitution = useCallback(async (financialInstitutionResource) => {
    resource.attr('selectedFinancialInstitutionID', financialInstitutionResource.id());
    try {
      setModalCollection(await resource.submit());
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }, [resource, setModalCollection, displayErrorMessage]);

  const onSearch = useCallback((searchTerm) => resource.query(
    'getFinancialInstitutions',
    (link) => {
      // Not doing a .copy() because it's too slow (there are 100 financial institutions in the attributes in the parent of the link).
      // This is safe because we got this link from .query, which constructed a new link.
      // The only way we could be modifying the original resource is if we touched link.parent, but we're not
      const linkWithStepType = link.withQueryParams().like('search', searchTerm);

      return linkWithStepType.get();
    },
    () => { throw new Error('Expected the getFinancialInstitutions query'); },
  ), [resource]);

  // This query has to exist for this page to work - just look at the above onSearch which uses the same query.
  const searchPrompt = resource.meta().query('getFinancialInstitutions').attr('prompt');

  return (
    <>
      <MetaControlField attr={'title'} />
      <DialogContent scrollKey={resource?.type() + resource?.id()}>
        <FDStack direction="column" spacing={2} maxHeight="100%">
          <MetaControlField attr={'subtitle'} />
          <SearchableFinancialInstitutionList
            resultsOverflow="auto"
            startingInstitutions={startingInstitutions}
            onSearch={onSearch}
            searchPrompt={searchPrompt}
            onClickInstitution={onClickInstitution}
            onClickManualInstitutionAdd={onClickManualInstitutionAdd}
            manualInstitutionAddDescription={manualInstitutionAddDescription}
            manualInstitutionAddPrompt={manualInstitutionAddPrompt}
          />
        </FDStack>
      </DialogContent>
    </>
  );
};
